// ---------------------------------------------------------------------------
// ContextToolbar — floating action toolbar shown above the selected element.
//
// Dynamic based on selectionTier:
//   - block:     [+ Add Element] [Move Up] [Move Down] | [Duplicate] [Delete]
//   - component (container): [+ Add Child] [Move Up] [Move Down] | [Duplicate] [Wrap] [Delete]
//   - component (regular):   [Move Up] [Move Down] | [Duplicate] [Wrap] [Delete]
//
// Positioned by selection-overlay.tsx above the element's bounding rect.
// ---------------------------------------------------------------------------

import { useCallback, useState, useRef, useEffect } from 'react'
import {
  Trash2,
  CopyPlus,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Plus,
  BoxSelect,
  Square,
  Columns2,
  LayoutGrid,
  Type,
  Heading,
  Image,
  MousePointerClick,
  TextCursorInput,
} from 'lucide-react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { createElement, isContainer } from '@/features/page-editor/lib/element-factory'
import { findElementInTree, findParentInTree } from '@/features/page-editor/store/canvas-slice'
import { cn } from '@/lib/utils'
import { SHORTCUT } from '@/lib/shortcuts'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// ---------------------------------------------------------------------------
// Add Element / Add Child dropdown items
// ---------------------------------------------------------------------------

interface AddChildItem {
  type: string
  label: string
  icon: React.ReactNode
  category: string
}

const ADD_CHILD_ITEMS: AddChildItem[] = [
  // Layout
  { type: 'container', label: 'Container', icon: <Square className="h-3.5 w-3.5" />, category: 'Layout' },
  { type: 'row', label: 'Row', icon: <Columns2 className="h-3.5 w-3.5" />, category: 'Layout' },
  { type: 'grid', label: 'Grid', icon: <LayoutGrid className="h-3.5 w-3.5" />, category: 'Layout' },
  // Content
  { type: 'text', label: 'Text', icon: <Type className="h-3.5 w-3.5" />, category: 'Content' },
  { type: 'heading', label: 'Heading', icon: <Heading className="h-3.5 w-3.5" />, category: 'Content' },
  { type: 'image', label: 'Image', icon: <Image className="h-3.5 w-3.5" />, category: 'Content' },
  // Form
  { type: 'button', label: 'Button', icon: <MousePointerClick className="h-3.5 w-3.5" />, category: 'Form' },
  { type: 'input', label: 'Input', icon: <TextCursorInput className="h-3.5 w-3.5" />, category: 'Form' },
]

// ---------------------------------------------------------------------------
// Toolbar button component
// ---------------------------------------------------------------------------

function ToolbarButton({
  icon,
  label,
  onClick,
  disabled = false,
  variant = 'default',
  testId,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
  variant?: 'default' | 'destructive'
  testId?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded transition-colors',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            variant === 'destructive'
              ? 'text-destructive hover:bg-destructive/10'
              : 'text-foreground/70 hover:bg-accent hover:text-foreground',
          )}
          onClick={onClick}
          disabled={disabled}
          data-testid={testId ?? `ctx-btn-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// Dropdown for adding elements / children
// ---------------------------------------------------------------------------

function AddElementDropdown({
  open,
  onToggle,
  onSelect,
  label,
  testId,
}: {
  open: boolean
  onToggle: () => void
  onSelect: (type: string) => void
  label: string
  testId: string
}) {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onToggle()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onToggle])

  return (
    <div className="relative" ref={dropdownRef}>
      <ToolbarButton
        icon={<Plus className="h-3.5 w-3.5" />}
        label={label}
        onClick={onToggle}
        testId={testId}
      />

      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-40 rounded-xl border bg-popover p-1 shadow-lg"
          data-testid="add-child-dropdown"
        >
          {['Layout', 'Content', 'Form'].map((category) => (
            <div key={category}>
              <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                {category}
              </div>
              {ADD_CHILD_ITEMS.filter((item) => item.category === category).map(
                (item) => (
                  <button
                    key={item.type}
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
                    onClick={() => onSelect(item.type)}
                    data-testid={`add-child-${item.type}`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                ),
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Block-tier toolbar
// ---------------------------------------------------------------------------

function BlockToolbar({ blockName }: { blockName: string }) {
  const [addOpen, setAddOpen] = useState(false)

  const activePageId = useEditorStore((s) => s.activePageId)
  const app = useEditorStore((s) => s.app)
  const block = useEditorStore((s) => s.blocks[blockName])

  // Find block position in page
  const activePage = app?.pages.find((p) => p.id === activePageId)
  const blockIndex = activePage?.blocks.indexOf(blockName) ?? -1
  const canMoveUp = blockIndex > 0
  const canMoveDown = activePage ? blockIndex < activePage.blocks.length - 1 : false

  const handleMoveUp = useCallback(() => {
    if (!activePageId || blockIndex < 1) return
    useEditorStore.getState().reorderPageBlocks(activePageId, blockIndex, blockIndex - 1)
  }, [activePageId, blockIndex])

  const handleMoveDown = useCallback(() => {
    if (!activePageId || !activePage || blockIndex >= activePage.blocks.length - 1) return
    useEditorStore.getState().reorderPageBlocks(activePageId, blockIndex, blockIndex + 1)
  }, [activePageId, activePage, blockIndex])

  const handleDelete = useCallback(() => {
    if (!activePageId) return
    useEditorStore.getState().removeBlockFromPage(activePageId, blockName)
    useEditorStore.getState().clearSelection()
  }, [activePageId, blockName])

  const handleAddElement = useCallback(
    (type: string) => {
      if (!block) return
      const newEl = createElement(type)
      // Add as child of block root
      useEditorStore.getState().addChildElement(blockName, block.ui.root.id, newEl)
      setAddOpen(false)
      useEditorStore.getState().select(newEl.id, blockName)
    },
    [blockName, block],
  )

  if (!block) return null

  return (
    <>
      {/* Add Element */}
      <AddElementDropdown
        open={addOpen}
        onToggle={() => setAddOpen(!addOpen)}
        onSelect={handleAddElement}
        label="Add Element"
        testId="ctx-btn-add-element"
      />

      {/* Move Up */}
      <ToolbarButton
        icon={<ArrowUp className="h-3.5 w-3.5" />}
        label="Move Up"
        onClick={handleMoveUp}
        disabled={!canMoveUp}
        testId="ctx-btn-move-up"
      />

      {/* Move Down */}
      <ToolbarButton
        icon={<ArrowDown className="h-3.5 w-3.5" />}
        label="Move Down"
        onClick={handleMoveDown}
        disabled={!canMoveDown}
        testId="ctx-btn-move-down"
      />

      {/* Separator */}
      <div className="mx-0.5 h-4 w-px bg-border" />

      {/* Duplicate — disabled for blocks (complex deep-clone needed) */}
      <ToolbarButton
        icon={<CopyPlus className="h-3.5 w-3.5" />}
        label="Duplicate"
        onClick={() => {}}
        disabled
        testId="ctx-btn-duplicate"
      />

      {/* Delete */}
      <ToolbarButton
        icon={<Trash2 className="h-3.5 w-3.5" />}
        label={`Delete (${SHORTCUT.delete})`}
        onClick={handleDelete}
        variant="destructive"
        testId="ctx-btn-delete"
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Component-tier toolbar (container or regular element)
// ---------------------------------------------------------------------------

function ComponentToolbar({
  selectedIds,
  blockName,
}: {
  selectedIds: string[]
  blockName: string
}) {
  const [addChildOpen, setAddChildOpen] = useState(false)

  const blocks = useEditorStore((s) => s.blocks)

  const elementId = selectedIds[0]
  const block = blocks[blockName]
  const element = block ? findElementInTree(block.ui.root, elementId) : undefined
  const parentInfo = block ? findParentInTree(block.ui.root, elementId) : undefined

  const canMoveUp = parentInfo != null && parentInfo.index > 0
  const canMoveDown =
    parentInfo != null &&
    parentInfo.parent.children != null &&
    parentInfo.index < parentInfo.parent.children.length - 1
  const isContainerEl = element != null && isContainer(element)
  const isRoot = parentInfo == null

  // Detect parent layout direction for move button icons
  const parentLayout = parentInfo?.parent.layout
  const parentDirection =
    parentLayout?.type === 'grid'
      ? 'grid'
      : parentInfo?.parent.type === 'row' || parentLayout?.direction === 'row'
        ? 'row'
        : 'column'
  const isHorizontal = parentDirection === 'row' || parentDirection === 'grid'

  // -- Actions --

  const handleDelete = useCallback(() => {
    useEditorStore.getState().removeElement(blockName, elementId)
    useEditorStore.getState().clearSelection()
  }, [blockName, elementId])

  const handleDuplicate = useCallback(() => {
    useEditorStore.getState().duplicateSelection()
  }, [])

  const handleMoveUp = useCallback(() => {
    useEditorStore.getState().moveElement(blockName, elementId, 'up')
  }, [blockName, elementId])

  const handleMoveDown = useCallback(() => {
    useEditorStore.getState().moveElement(blockName, elementId, 'down')
  }, [blockName, elementId])

  const handleWrap = useCallback(() => {
    useEditorStore.getState().wrapElement(blockName, elementId)
  }, [blockName, elementId])

  const handleAddChild = useCallback(
    (type: string) => {
      const newEl = createElement(type)
      useEditorStore.getState().addChildElement(blockName, elementId, newEl)
      setAddChildOpen(false)
      useEditorStore.getState().select(newEl.id, blockName)
    },
    [blockName, elementId],
  )

  if (!element) return null

  return (
    <>
      {/* Add Child — only for containers */}
      {isContainerEl && (
        <AddElementDropdown
          open={addChildOpen}
          onToggle={() => setAddChildOpen(!addChildOpen)}
          onSelect={handleAddChild}
          label="Add Child"
          testId="ctx-btn-add-element"
        />
      )}

      {/* Move Before (Up or Left depending on parent direction) */}
      <ToolbarButton
        icon={isHorizontal
          ? <ArrowLeft className="h-3.5 w-3.5" />
          : <ArrowUp className="h-3.5 w-3.5" />}
        label={isHorizontal ? 'Move Left' : 'Move Up'}
        onClick={handleMoveUp}
        disabled={!canMoveUp}
        testId="ctx-btn-move-up"
      />

      {/* Move After (Down or Right depending on parent direction) */}
      <ToolbarButton
        icon={isHorizontal
          ? <ArrowRight className="h-3.5 w-3.5" />
          : <ArrowDown className="h-3.5 w-3.5" />}
        label={isHorizontal ? 'Move Right' : 'Move Down'}
        onClick={handleMoveDown}
        disabled={!canMoveDown}
        testId="ctx-btn-move-down"
      />

      {/* Separator */}
      <div className="mx-0.5 h-4 w-px bg-border" />

      {/* Duplicate */}
      <ToolbarButton
        icon={<CopyPlus className="h-3.5 w-3.5" />}
        label={`Duplicate (${SHORTCUT.duplicate})`}
        onClick={handleDuplicate}
        disabled={isRoot}
        testId="ctx-btn-duplicate"
      />

      {/* Wrap in Container */}
      <ToolbarButton
        icon={<BoxSelect className="h-3.5 w-3.5" />}
        label="Wrap"
        onClick={handleWrap}
        disabled={isRoot}
        testId="ctx-btn-wrap"
      />

      {/* Separator */}
      <div className="mx-0.5 h-4 w-px bg-border" />

      {/* Delete */}
      <ToolbarButton
        icon={<Trash2 className="h-3.5 w-3.5" />}
        label={`Delete (${SHORTCUT.delete})`}
        onClick={handleDelete}
        variant="destructive"
        disabled={isRoot}
        testId="ctx-btn-delete"
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Main ContextToolbar component
// ---------------------------------------------------------------------------

export function ContextToolbar({
  selectedIds,
  blockName,
}: {
  selectedIds: string[]
  blockName: string
}) {
  const selectionTier = useEditorStore((s) => s.selectionTier)

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex items-center gap-0.5 rounded-xl border bg-popover px-1 py-0.5 shadow-md"
        data-testid="context-toolbar"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {selectionTier === 'block' ? (
          <BlockToolbar blockName={blockName} />
        ) : (
          <ComponentToolbar selectedIds={selectedIds} blockName={blockName} />
        )}
      </div>
    </TooltipProvider>
  )
}
